import { useState, useEffect, useRef, useId } from "react";
import { useAuthStore } from "@/store/useAuthStore";
import {
  getStoredFormData,
  saveFormData,
} from "@/features/tutor/context/formStorage";
import { initialFormData } from "@/features/tutor/context/FormContext";
import { recordPreferredVoice } from "@/features/tutor/context/preferredVoice";
import api from "@/utils/axios-utils";
import {
  useNamePronunciationAudio,
  type NamePronunciationEntryResponse,
} from "@/hooks/useNamePronunciationAudio";
import ALL_LANGUAGE_OPTIONS from "@/constants/multilingual";
import {
  AUDIO_VOICE_MAP,
  getVoicesForLanguage,
  getBetaVoicesForLanguage,
  getLocalizedName,
  getNamePronunciationKey,
  supportsNamePronunciationTraining,
  type AudioVoice,
  type TTSProvider,
} from "@/constants/audioVoice";
import SpeakingPersonIcon from "@/components/icons/SpeakingPersonIcon";

// Same language list as the session initialization modal, without the multilingual/auto option
const LANGUAGE_OPTIONS = ALL_LANGUAGE_OPTIONS.filter((l) => !l.multilingual);

interface NameAndVoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface VoicePref {
  chosenVoiceId: string;
  namePronunciations: Record<string, { spelling: string; trainedAt: number }>;
  configuredVoiceIds?: string[];
  chosenLang?: string;
}

// ---- Utilities ----

function getStorageKey(userId: string | number) {
  return `student_voice_pref_${userId}`;
}

function getVoiceProviderConfig(voice: AudioVoice): {
  provider: TTSProvider;
  providerVoiceId: string;
} {
  // Pronunciation is Cartesia-only; "" for non-cartesia voices disables the
  // save/train path (callers guard on a truthy providerVoiceId).
  return {
    provider: voice.ttsProvider,
    providerVoiceId:
      voice.ttsProvider === "cartesia" ? voice.providerVoiceId : "",
  };
}

function loadVoicePref(userId: string | number): VoicePref {
  try {
    const raw = localStorage.getItem(getStorageKey(userId));
    if (raw) {
      // Back-compat: older clients stored this map under `pronunciations`.
      const parsed = JSON.parse(raw) as VoicePref & {
        pronunciations?: VoicePref["namePronunciations"];
      };
      const namePronunciations =
        parsed.namePronunciations ?? parsed.pronunciations ?? {};
      // Migrate old string values to new { spelling, trainedAt } shape
      for (const key in namePronunciations) {
        if (typeof (namePronunciations[key] as unknown) === "string") {
          namePronunciations[key] = {
            spelling: namePronunciations[key] as unknown as string,
            trainedAt: 0,
          };
        }
      }
      return {
        chosenVoiceId: parsed.chosenVoiceId,
        namePronunciations,
        configuredVoiceIds: parsed.configuredVoiceIds,
        chosenLang: parsed.chosenLang,
      };
    }
  } catch {
    /* Invalid or unavailable local preferences fall back to defaults. */
  }
  return {
    chosenVoiceId: "chloe",
    namePronunciations: {},
    configuredVoiceIds: [],
    chosenLang: undefined,
  };
}

function saveVoicePref(userId: string | number, pref: VoicePref) {
  try {
    // Also write the old `pronunciations` key so a browser tab still
    // running a pre-deploy bundle (which only reads `pronunciations`)
    // doesn't crash on `pref.pronunciations[...]` being undefined.
    const toStore = { ...pref, pronunciations: pref.namePronunciations };
    localStorage.setItem(getStorageKey(userId), JSON.stringify(toStore));
  } catch {
    /* Preferences may be unavailable in private browsing. */
  }
}

// ---- VoiceCard sub-component ----

interface VoiceCardProps {
  voice: AudioVoice;
  displayName: string;
  isChosen: boolean;
  isPlaying: boolean;
  hasCustomPron: boolean;
  onPlay: (e: React.MouseEvent) => void;
  onSelect: () => void;
  onPlayName?: (e: React.MouseEvent) => void;
}

const VoiceCard = ({
  voice,
  displayName,
  isChosen,
  isPlaying,
  hasCustomPron,
  onPlay,
  onSelect,
  onPlayName,
}: VoiceCardProps) => (
  <div
    onClick={onSelect}
    className={`et-voice-option relative rounded-[12px] border cursor-pointer transition-all p-3.5 ${
      isChosen
        ? "border-[#28543D] bg-[#EEF0E3] shadow-[0_0_0_3px_rgba(40,84,61,0.1)]"
        : "border-[#E7EBEE] bg-white hover:border-[#CFD6DB] hover:shadow-sm"
    }`}
  >
    <input
      type="radio"
      name="tutor-voice"
      checked={isChosen}
      onChange={onSelect}
      aria-label={`Select ${displayName}`}
      className="absolute right-3 top-3 accent-emerald-700"
    />
    {isChosen && (
      <span className="absolute -top-[9px] left-3 rounded-full bg-[#EEF0E3] px-2.5 py-0 text-[10px] font-semibold text-[#28543D] shadow-[0_0_0_2px_white] leading-[18px]">
        Currently Selected
      </span>
    )}
    <div className="flex items-center gap-2 mb-2">
      <button
        type="button"
        onClick={onPlay}
        aria-label={`Preview ${displayName}`}
        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border transition-all ${
          isPlaying
            ? "bg-[#28543D] border-[#28543D] text-white"
            : isChosen
              ? "bg-white border-[#DED8CC] text-[#28543D] hover:bg-[#28543D] hover:border-[#28543D] hover:text-white"
              : "bg-[#EEF0E3] border-[#DED8CC] text-[#28543D] hover:bg-[#28543D] hover:border-[#28543D] hover:text-white"
        }`}
      >
        {isPlaying ? (
          <svg viewBox="0 0 16 16" width="10" height="10" fill="currentColor">
            <rect x="4" y="3" width="3" height="10" />
            <rect x="9" y="3" width="3" height="10" />
          </svg>
        ) : (
          <svg viewBox="0 0 16 16" width="10" height="10" fill="currentColor">
            <polygon points="4,3 13,8 4,13" />
          </svg>
        )}
      </button>
      <span className="font-nunito font-extrabold text-[15px] text-[#294B39] leading-tight">
        {displayName}
      </span>
      <span
        className={`rounded px-1.5 py-[2px] text-[10px] font-bold uppercase tracking-wide flex-shrink-0 ${
          voice.gender === "f"
            ? "bg-[#FDE7EF] text-[#8A3457]"
            : "bg-[#E4EEF8] text-[#2A4F7A]"
        }`}
      >
        {voice.gender === "f" ? "Feminine" : "Masculine"}
      </span>
      {hasCustomPron && (
        <button
          type="button"
          onClick={onPlayName}
          className="inline-flex items-center justify-center w-[26px] h-[26px] flex-shrink-0 rounded-full bg-[#EEF0E3] text-[#28543D] border-none cursor-pointer p-0 transition-colors hover:bg-[#E2E9DC]"
          title="Says your name right — click to hear it"
          aria-label="Play saved name pronunciation"
        >
          <SpeakingPersonIcon />
        </button>
      )}
    </div>
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {[voice.accent, voice.tone, voice.range].map((tag, i) => (
        <span
          key={i}
          className="rounded-full bg-[#F4F5F6] px-2 py-[2px] text-[11px] leading-none text-[#3E4C53]"
        >
          {tag}
        </span>
      ))}
    </div>
  </div>
);

// ---- Main component ----

const NameAndVoiceModal = ({ isOpen, onClose }: NameAndVoiceModalProps) => {
  const { user } = useAuthStore();

  // Beta voices appended only for flagged users, and only if their provider
  // actually supports name pronunciation training (see #25 in PR review notes).
  const getVisibleVoices = (lang: string): AudioVoice[] =>
    user?.config?.enableBetaTtsProviders
      ? [
          ...getVoicesForLanguage(lang),
          ...getBetaVoicesForLanguage(lang).filter(
            supportsNamePronunciationTraining,
          ),
        ]
      : getVoicesForLanguage(lang);

  const [chosenVoiceId, setChosenVoiceId] = useState("chloe");
  const [activeLang, setActiveLang] = useState("");
  const [configuredVoiceIds, setConfiguredVoiceIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const [namePronunciations, setNamePronunciations] = useState<
    Record<string, { spelling: string; trainedAt: number }>
  >({});
  const [savedEntries, setSavedEntries] = useState<
    Record<string, NamePronunciationEntryResponse>
  >({});
  const [isLoadingEntries, setIsLoadingEntries] = useState(false);
  const [spellInput, setSpellInput] = useState("");
  const [showVoiceChangeWarning, setShowVoiceChangeWarning] = useState(false);
  const [saved, setSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const langDropdownRef = useRef<HTMLDivElement>(null);
  const langTriggerRef = useRef<HTMLButtonElement>(null);
  const langOptionsId = useId();
  const studentFirstName =
    user?.displayUsername || user?.name?.split(" ")[0] || "";
  const hasFirstName = Boolean(studentFirstName);

  const chosenVoice =
    AUDIO_VOICE_MAP[chosenVoiceId] ?? AUDIO_VOICE_MAP["chloe"];

  const {
    isSpeakingBack,
    isPlayingCurrent,
    lastPhonemes,
    audioError,
    setAudioError,
    clearLastPhonemes,
    speakBack,
    playCurrent,
    resolvePhonemes,
    stop,
  } = useNamePronunciationAudio();

  useEffect(() => {
    if (isOpen) {
      if (user?.id) {
        const pref = loadVoicePref(user.id);
        const sessionForm = getStoredFormData(initialFormData);
        const resolvedVoiceId =
          sessionForm?.voice?.voiceId &&
          AUDIO_VOICE_MAP[sessionForm.voice.voiceId]
            ? sessionForm.voice.voiceId
            : pref.chosenVoiceId;
        const resolvedLang =
          sessionForm?.language?.value ??
          (pref.chosenLang && pref.chosenLang !== "all"
            ? pref.chosenLang
            : "en");
        setChosenVoiceId(resolvedVoiceId);
        setNamePronunciations(pref.namePronunciations);
        setConfiguredVoiceIds(pref.configuredVoiceIds ?? []);
        const resolvedVoice =
          AUDIO_VOICE_MAP[resolvedVoiceId] ?? AUDIO_VOICE_MAP["chloe"];
        const initPronKey = getNamePronunciationKey(
          resolvedVoice,
          resolvedLang,
        );
        setSpellInput(
          pref.namePronunciations[initPronKey]?.spelling ?? studentFirstName,
        );
        setActiveLang(resolvedLang);
        setSearchQuery("");
        setShowVoiceChangeWarning(false);
        setPlayingVoiceId(null);
        setSaved(false);
        setIsSaving(false);
        setAudioError(null);
        setIsLangDropdownOpen(false);
        setSavedEntries({});
        setIsLoadingEntries(true);
        stopPreviewAudio();

        // Merge DDB entries into namePronunciations (DDB wins — keys are provider:providerVoiceId:lang)
        void api
          .get<Record<string, NamePronunciationEntryResponse>>(
            "/name-pronunciation/entries",
          )
          .then((res) => {
            setSavedEntries(res.data);
            const ddbNamePronunciations = Object.fromEntries(
              Object.entries(res.data).map(([key, entry]) => [
                key,
                {
                  spelling: entry.surfaceText,
                  trainedAt: entry.updatedAtMs ?? 0,
                },
              ]),
            );
            setNamePronunciations((prev) => ({
              ...prev,
              ...ddbNamePronunciations,
            }));
          })
          .catch((err) => {
            console.error(
              "[NameAndVoiceModal] Failed to load pronunciation entries:",
              err,
            );
          })
          .finally(() => {
            setIsLoadingEntries(false);
          });
      }
    }
  }, [isOpen, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const pronKey = getNamePronunciationKey(chosenVoice, activeLang || "en");
    setSpellInput(namePronunciations[pronKey]?.spelling ?? studentFirstName);
  }, [chosenVoiceId, activeLang, namePronunciations, studentFirstName]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    clearLastPhonemes();
  }, [chosenVoiceId, activeLang]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      stopPreviewAudio();
      stop();
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isLangDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        langDropdownRef.current &&
        !langDropdownRef.current.contains(e.target as Node)
      ) {
        setIsLangDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isLangDropdownOpen]);

  const selectedLang =
    LANGUAGE_OPTIONS.find((l) => l.value === activeLang) ?? null;

  function stopPreviewAudio() {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current.onended = null;
      previewAudioRef.current.onerror = null;
    }
  }

  const handlePlayVoice = (v: AudioVoice, e: React.MouseEvent) => {
    e.stopPropagation();
    setAudioError(null);
    stopPreviewAudio();
    stop();
    if (playingVoiceId === v.id) {
      setPlayingVoiceId(null);
      return;
    }
    const s3Base = import.meta.env.VITE_S3_BUCKET_URL ?? "";
    const basePath = v.audioPreviews.medium;
    const localizedPath =
      activeLang && activeLang !== "en" && activeLang !== "auto"
        ? basePath.replace(".webm", `_${activeLang}.webm`)
        : basePath;
    const url = `${s3Base}${localizedPath}?v=4`;
    const audio = new Audio(url);
    previewAudioRef.current = audio;
    setPlayingVoiceId(v.id);
    audio.play().catch((err) => {
      // Pausing a pending play() rejects with AbortError: the user stopped
      // this preview or started another one, so there is nothing to report.
      if (err instanceof DOMException && err.name === "AbortError") return;
      console.error("[NameAndVoiceModal] play() failed:", err, "url:", url);
      setPlayingVoiceId(null);
      setAudioError("Voice preview could not play. Please try again.");
    });
    audio.onended = () => setPlayingVoiceId(null);
    audio.onerror = (err) => {
      console.error("[NameAndVoiceModal] audio load error:", err, "url:", url);
      setPlayingVoiceId(null);
      setAudioError("Voice preview could not load. Please try again.");
    };
  };

  const handleVoiceSelect = (v: AudioVoice) => {
    if (v.id === chosenVoiceId) return;
    setChosenVoiceId(v.id);
    const pronKey = getNamePronunciationKey(v, activeLang || "en");
    setShowVoiceChangeWarning(!savedEntries[pronKey]);
  };

  const handleSpellChange = (val: string) => {
    setSpellInput(val);
    clearLastPhonemes();
    const pronKey = getNamePronunciationKey(chosenVoice, activeLang || "en");
    setNamePronunciations((prev) => ({
      ...prev,
      [pronKey]: { spelling: val, trainedAt: prev[pronKey]?.trainedAt ?? 0 },
    }));
  };

  const handleSpeakBack = async () => {
    if (!hasFirstName || !spellInput) return;
    stop();
    stopPreviewAudio();
    setPlayingVoiceId(null);
    await speakBack(spellInput || studentFirstName, chosenVoice, activeLang);
  };

  const handlePlayCurrent = async () => {
    if (!hasFirstName) return;
    stop();
    stopPreviewAudio();
    setPlayingVoiceId(null);
    const lang = activeLang || "en";
    const savedEntry = savedEntries[getNamePronunciationKey(chosenVoice, lang)];
    await playCurrent(
      savedEntry?.surfaceText || studentFirstName,
      chosenVoice,
      lang,
      savedEntry,
    );
  };

  const handleSave = async () => {
    if (!user?.id || isSaving) return;
    setAudioError(null);
    const lang = activeLang || "en";
    const { provider, providerVoiceId } = getVoiceProviderConfig(chosenVoice);
    const pronKey = getNamePronunciationKey(chosenVoice, lang);
    const surfaceText = spellInput || studentFirstName;

    // Update localStorage immediately — doesn't depend on DDB completing.
    const newNamePronunciations = {
      ...namePronunciations,
      ...(hasFirstName
        ? { [pronKey]: { spelling: surfaceText, trainedAt: Date.now() } }
        : {}),
    };
    const newConfiguredVoiceIds =
      hasFirstName && newNamePronunciations[pronKey]?.spelling
        ? Array.from(new Set([chosenVoiceId, ...configuredVoiceIds]))
        : configuredVoiceIds;
    setConfiguredVoiceIds(newConfiguredVoiceIds);
    saveVoicePref(user.id, {
      chosenVoiceId,
      namePronunciations: newNamePronunciations,
      configuredVoiceIds: newConfiguredVoiceIds,
      chosenLang: activeLang || undefined,
    });

    // Outside FormProvider's tree, so no updateField(). A failed write here
    // only costs this browser's local cache (e.g. blocked localStorage on a
    // managed Chromebook) — it must not block the server-side saves below.
    const langOption = LANGUAGE_OPTIONS.find((l) => l.value === lang) ?? null;
    const currentSessionForm = getStoredFormData(initialFormData);
    // This modal has no speed control, so carry forward whatever is stored.
    const storedSpeed = currentSessionForm.voice.speed;
    let sessionFormSaved = true;
    if (langOption) {
      sessionFormSaved = saveFormData({
        ...currentSessionForm,
        voice: {
          voiceId: chosenVoiceId,
          speed: storedSpeed,
        },
        language: langOption,
      });
    }
    if (!sessionFormSaved) {
      console.warn(
        "[NameAndVoiceModal] sessionFormData write failed; continuing with server-side saves",
      );
    }

    // This modal's only commit point: no lesson start follows it. Gated on the
    // same langOption as the write above — this modal does not offer
    // multilingual, so it must not record a language it cannot represent.
    if (langOption) {
      recordPreferredVoice(chosenVoiceId, lang, storedSpeed);
    }

    if (!hasFirstName || !providerVoiceId) {
      setSaved(true);
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => setSaved(false), 2000);
      return;
    }

    setIsSaving(true);
    try {
      // Resolve phonemes: reuse lastPhonemes / cache before making an API call.
      const phonemesToSave =
        lastPhonemes ?? (await resolvePhonemes(surfaceText, chosenVoice, lang));

      if (!phonemesToSave) {
        setAudioError(
          "Could not generate pronunciation. Try clicking 'Speak it back' first, then save.",
        );
        return;
      }

      await api.put("/name-pronunciation/entries", {
        provider,
        providerVoiceId,
        lang,
        surfaceText,
        phonemes: phonemesToSave,
      });

      // Update badge only after confirmed DDB save.
      setSavedEntries((prev) => ({
        ...prev,
        [pronKey]: {
          provider,
          providerVoiceId,
          lang,
          surfaceText,
          phonemes: phonemesToSave!,
          updatedAtMs: Date.now(),
        },
      }));
      setShowVoiceChangeWarning(false);
      setSaved(true);
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error("[NameAndVoiceModal] Save failed:", err);
      setAudioError("Failed to save. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    stopPreviewAudio();
    stop();
    setPlayingVoiceId(null);
    onClose();
  };

  const filteredVoices = (() => {
    let voices: AudioVoice[];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      voices = getVisibleVoices(activeLang).filter(
        (v) =>
          v.name.toLowerCase().includes(q) ||
          v.accent.toLowerCase().includes(q) ||
          v.tone.toLowerCase().includes(q) ||
          v.range.toLowerCase().includes(q),
      );
    } else if (!activeLang) {
      voices = [];
    } else {
      voices = getVisibleVoices(activeLang);
    }
    // Sort voices with a saved pronunciation for current lang+voice combo to top
    voices = [
      ...voices.filter((v) =>
        Boolean(
          activeLang &&
            savedEntries[getNamePronunciationKey(v, activeLang || "en")],
        ),
      ),
      ...voices.filter(
        (v) =>
          !(
            activeLang &&
            savedEntries[getNamePronunciationKey(v, activeLang || "en")]
          ),
      ),
    ];
    return voices;
  })();

  const voicePicker = (
    <>
      {/* Section 2: Choose voice */}
      <div className="mb-4 sm:mb-6">
        <div className="flex items-baseline justify-between mb-3 gap-4">
          <h3 className="font-nunito font-bold text-[13px] sm:text-[15px] text-[#294B39] flex-shrink-0">
            2. Choose your tutor's voice
          </h3>
          <span className="font-mulish text-[12px] text-gray-400 text-right">
            Pronunciations are saved per voice.
          </span>
        </div>

        {/* Search */}
        <div className="flex items-center gap-2 bg-gray-50 rounded-[8px] px-3 py-2 mb-3 focus-within:ring-1 focus-within:ring-[#28543D]">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#28543D"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Search for a voice"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 bg-transparent font-mulish text-[13px] text-[#294B39] placeholder-gray-400 outline-none"
          />
        </div>

        {/* Voice grid */}
        <div className="border border-[#E0E0E0] rounded-[12px] p-3">
          <div className="max-h-[220px] sm:max-h-[320px] overflow-y-auto pr-1 -mr-1 pt-2">
            {filteredVoices.length === 0 ? (
              <div className="text-center py-10 font-mulish text-[13px] text-gray-400">
                {searchQuery
                  ? `No voices match "${searchQuery}"`
                  : !activeLang
                    ? "Select a language above to see available voices."
                    : "No voices available for this language."}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pb-1">
                {filteredVoices.map((v) => (
                  <VoiceCard
                    key={v.id}
                    voice={v}
                    displayName={getLocalizedName(v, activeLang)}
                    isChosen={v.id === chosenVoiceId}
                    isPlaying={playingVoiceId === v.id}
                    hasCustomPron={Boolean(
                      activeLang &&
                        savedEntries[
                          getNamePronunciationKey(v, activeLang || "en")
                        ],
                    )}
                    onPlay={(e) => handlePlayVoice(v, e)}
                    onSelect={() => handleVoiceSelect(v)}
                    onPlayName={(e) => {
                      e.stopPropagation();
                      const saved =
                        savedEntries[
                          getNamePronunciationKey(v, activeLang || "en")
                        ];
                      if (saved && !isPlayingCurrent && !isSpeakingBack) {
                        stop();
                        stopPreviewAudio();
                        setPlayingVoiceId(null);
                        void playCurrent(
                          saved.surfaceText,
                          v,
                          activeLang || "en",
                          saved,
                        );
                      }
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
  if (!isOpen) return null;

  return (
    <>
      <div>
        <div className="et-settings-form et-voice-settings">
          {/* Header */}
          <div className="flex items-start justify-between mb-1">
            <h2 className="font-nunito font-extrabold text-[22px] sm:text-[26px] text-[#294B39]">
              Name &amp; Voice
            </h2>
          </div>
          <p className="font-mulish text-[13px] text-gray-400 mb-4 sm:mb-6">
            Pick a language first, then choose the voice you want your tutor to
            use.
          </p>

          {/* Section 1: Language */}
          <div className="et-language-field mb-4 sm:mb-6">
            <h3 className="font-nunito font-bold text-[13px] sm:text-[15px] text-[#294B39] mb-3">
              1. Pick a language
            </h3>
            <div
              className="relative max-w-[360px]"
              ref={langDropdownRef}
              onKeyDown={(event) => {
                if (event.key === "Escape" && isLangDropdownOpen) {
                  event.stopPropagation();
                  setIsLangDropdownOpen(false);
                  langTriggerRef.current?.focus();
                }
              }}
            >
              {/* Trigger */}
              <button
                type="button"
                ref={langTriggerRef}
                aria-expanded={isLangDropdownOpen}
                aria-controls={isLangDropdownOpen ? langOptionsId : undefined}
                onClick={() => setIsLangDropdownOpen((prev) => !prev)}
                className={`flex items-center gap-2.5 w-full px-3.5 py-3 bg-white border-[1.5px] rounded-[12px] cursor-pointer text-left transition-colors ${
                  isLangDropdownOpen
                    ? "border-[#28543D]"
                    : "border-[#E7EBEE] hover:border-[#28543D]"
                }`}
              >
                {selectedLang?.icon ? (
                  <img
                    src={selectedLang.icon}
                    alt=""
                    className="h-[14px] w-[20px] rounded-[2px] object-cover shadow-[0_0_0_1px_rgba(0,0,0,0.06)] flex-shrink-0"
                  />
                ) : (
                  <span className="w-[20px] h-[14px] flex-shrink-0" />
                )}
                <span className="flex-1 font-mulish font-semibold text-[13px] text-[#1F2A2F] min-w-0 truncate">
                  {selectedLang ? selectedLang.label : "Select a language"}
                </span>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={`text-gray-400 flex-shrink-0 transition-transform duration-150 ${isLangDropdownOpen ? "rotate-180" : ""}`}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {/* Dropdown menu */}
              {isLangDropdownOpen && (
                <ul
                  id={langOptionsId}
                  aria-label="Languages"
                  className="absolute top-[calc(100%+6px)] left-0 right-0 max-h-[280px] overflow-y-auto bg-white border-[1.5px] border-[#E7EBEE] rounded-[12px] shadow-[0_8px_24px_rgba(0,0,0,0.08)] p-1.5 z-20 list-none m-0"
                >
                  {LANGUAGE_OPTIONS.map((lang) => {
                    const isSelected = activeLang === lang.value;
                    return (
                      <li key={lang.value}>
                        <button
                          type="button"
                          aria-pressed={isSelected}
                          onClick={() => {
                            const voices = getVisibleVoices(lang.value);
                            const voiceAvailable = voices.some(
                              (v) => v.id === chosenVoiceId,
                            );
                            if (!voiceAvailable && voices.length > 0) {
                              setChosenVoiceId(voices[0].id);
                              setShowVoiceChangeWarning(false);
                            }
                            setActiveLang(lang.value);
                            setIsLangDropdownOpen(false);
                            langTriggerRef.current?.focus();
                          }}
                          className={`flex w-full text-left items-center gap-2.5 px-3 py-2.5 rounded-[8px] cursor-pointer transition-colors ${
                            isSelected ? "bg-[#EEF0E3]" : "hover:bg-[#EEF0E3]"
                          }`}
                        >
                          {lang.icon && (
                            <img
                              src={lang.icon}
                              alt=""
                              className="h-[14px] w-[20px] rounded-[2px] object-cover shadow-[0_0_0_1px_rgba(0,0,0,0.06)] flex-shrink-0"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <span
                              className={`font-mulish font-semibold text-[13px] leading-tight ${isSelected ? "text-[#28543D]" : "text-[#1F2A2F]"}`}
                            >
                              {lang.label}
                            </span>
                            {lang.sublabel1 && (
                              <span className="text-[11px] text-[#7A8891] font-medium ml-1">
                                {lang.sublabel1}
                              </span>
                            )}
                          </div>
                          {isSelected && (
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#28543D"
                              strokeWidth="3"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="flex-shrink-0"
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          <div className="et-voice-picker">{voicePicker}</div>

          {/* Section 3: Pronunciation */}
          <div className="et-pronunciation mb-4 sm:mb-6">
            <h3 className="font-nunito font-bold text-[13px] sm:text-[15px] text-[#294B39] mb-1 flex items-center gap-2">
              How should{" "}
              <span className="text-[#28543D]">
                {getLocalizedName(chosenVoice, activeLang)}
              </span>{" "}
              say your name?
              {isLoadingEntries && (
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  className="animate-spin text-[#28543D] flex-shrink-0"
                >
                  <circle cx="12" cy="12" r="10" strokeOpacity="0.3" />
                  <path d="M12 2a10 10 0 0 1 10 10" />
                </svg>
              )}
            </h3>

            {/* Step hint — pairing label (R5 copy, mandatory) */}
            {savedEntries[
              getNamePronunciationKey(chosenVoice, activeLang || "en")
            ] && (
              <div className="flex items-center gap-1.5 mb-3">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#28543D"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-[14px] h-[14px] flex-shrink-0"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <p className="font-mulish text-[12px] text-[#28543D]">
                  Saved for{" "}
                  <strong>{getLocalizedName(chosenVoice, activeLang)}</strong> ·{" "}
                  <strong>
                    {LANGUAGE_OPTIONS.find((l) => l.value === activeLang)
                      ?.label ?? activeLang}
                  </strong>
                  . Switching either voice or language means re-teaching.
                </p>
              </div>
            )}

            {showVoiceChangeWarning && (
              <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-[10px] px-3 py-3 mb-4 font-mulish text-[12px] text-amber-700">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="flex-shrink-0"
                >
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                <div>
                  <strong className="font-nunito font-bold">Heads up:</strong>{" "}
                  <strong className="font-nunito font-bold">
                    {getLocalizedName(chosenVoice, activeLang)}
                  </strong>{" "}
                  hasn't been taught your name yet in{" "}
                  <strong className="font-nunito font-bold">
                    {LANGUAGE_OPTIONS.find((l) => l.value === activeLang)
                      ?.label ?? activeLang}
                  </strong>
                  . Spell it below and save.
                </div>
              </div>
            )}

            {/* Current pronunciation preview card */}
            <div className="et-pronunciation-preview">
              <button
                type="button"
                onClick={isPlayingCurrent ? stop : handlePlayCurrent}
                disabled={!hasFirstName && !isPlayingCurrent}
              >
                {isPlayingCurrent
                  ? "Stop pronunciation"
                  : "Hear current pronunciation"}
              </button>
              <p>{studentFirstName || "—"}</p>
            </div>
            <label className="block font-nunito font-semibold text-[13px] text-gray-500 mb-2">
              Spell it the way it should sound
            </label>
            <input
              type="text"
              value={spellInput}
              aria-label="Name pronunciation"
              onChange={(e) => handleSpellChange(e.target.value)}
              disabled={!hasFirstName}
              placeholder={studentFirstName || "Your name"}
              className={`w-full h-[44px] px-4 border border-[#E0E0E0] rounded-[10px] font-nunito font-bold text-[15px] text-[#294B39] focus:outline-none focus:border-[#28543D] focus:shadow-[0_0_4px_0_#28543D4D] ${!hasFirstName ? "bg-gray-50 cursor-not-allowed opacity-50" : "bg-white"}`}
            />
            <p className="font-mulish text-[12px] text-gray-400 mt-2 leading-relaxed">
              Type your name the way it sounds, using hyphens for syllables.
              We'll figure out the rest.
            </p>

            {/* Speak it back */}
            <div className="et-speak-back mt-3 bg-gray-50 rounded-[10px] p-4">
              <button
                type="button"
                onClick={isSpeakingBack ? stop : handleSpeakBack}
                disabled={!isSpeakingBack && (!hasFirstName || !spellInput)}
                className="flex items-center gap-2 px-4 py-2 border border-[#28543D] bg-white text-[#28543D] font-nunito font-bold text-[13px] rounded-[8px] hover:bg-[#EEF0E3] transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSpeakingBack ? (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    className="animate-spin"
                  >
                    <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                    <path d="M12 2a10 10 0 0 1 10 10" />
                  </svg>
                ) : (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="8" cy="7" r="3" />
                    <path d="M3 21v-1a5 5 0 0 1 5-5h0a5 5 0 0 1 5 5v1" />
                    <path d="M16 8a3 3 0 0 1 0 4" />
                    <path d="M19 6a6 6 0 0 1 0 8" />
                  </svg>
                )}
                Speak it back
              </button>
            </div>
            {audioError && (
              <p className="font-mulish text-[12px] text-[#C2410C] mt-2">
                {audioError}
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="et-settings-actions flex items-center gap-4 mt-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="h-[52px] px-6 bg-[#28543D] text-white font-nunito font-bold text-[16px] rounded-[12px] hover:bg-[#204833] transition-colors flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    className="animate-spin"
                  >
                    <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                    <path d="M12 2a10 10 0 0 1 10 10" />
                  </svg>
                  Saving...
                </>
              ) : saved ? (
                <>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Saved!
                </>
              ) : (
                "Save pronunciation"
              )}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="font-nunito font-semibold text-[16px] text-[#28543D] hover:text-[#28543D]/80 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default NameAndVoiceModal;
export { loadVoicePref, saveVoicePref, getStorageKey, getVoiceProviderConfig };
